import { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ActivityHeatmapDay,
  buildActivityHeatmapAxisLabels,
  buildActivityHeatmapWeeks,
  getHeatmapCellColor,
  getPeakActivityDay,
} from "../../constants/dashboardStats";
import "./ActivityHeatmap.scss";

type ActivityHeatmapProps = {
  days: ActivityHeatmapDay[];
};

const HEATMAP_LEGEND_COLORS = ["#eef2f0", "#c6ebd9", "#8fd9b8", "#4fbf8a", "#1d9e75"];

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const ActivityHeatmap = ({ days }: ActivityHeatmapProps) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [hoveredDay, setHoveredDay] = useState<ActivityHeatmapDay | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const maxQuestions = useMemo(
    () => (days.length > 0 ? Math.max(...days.map((day) => day.questions), 1) : 1),
    [days]
  );
  const weeks = useMemo(
    () => (days.length > 0 ? buildActivityHeatmapWeeks(days) : []),
    [days]
  );
  const axisLabels = useMemo(
    () => buildActivityHeatmapAxisLabels(weeks),
    [weeks]
  );
  const daysByDate = useMemo(
    () => new Map(days.map((day) => [day.date, day])),
    [days]
  );
  const peakDay = useMemo(() => getPeakActivityDay(days), [days]);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    grid.scrollLeft = grid.scrollWidth - grid.clientWidth;
  }, [weeks]);

  const handleGridMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    setTooltipPos({ x: event.clientX, y: event.clientY });

    const cell = (event.target as HTMLElement).closest<HTMLElement>(
      ".activity-heatmap__cell:not(.activity-heatmap__cell--empty)"
    );
    if (!cell) return;

    const date = cell.dataset.date;
    const day = date ? daysByDate.get(date) : undefined;
    if (day) setHoveredDay(day);
  };

  const hideTooltip = () => {
    setHoveredDay(null);
  };

  return (
    <div className="activity-heatmap">
      <div className="activity-heatmap__header">
        <div>
          <h4 className="activity-heatmap__title">Daily activity</h4>
          <p className="activity-heatmap__subtitle">
            Last year in this question bank.
            {peakDay ? (
              <>
                {" "}
                Most questions in one day:{" "}
                <strong>
                  {peakDay.questions} on {peakDay.fullDateLabel}
                </strong>
                .
              </>
            ) : null}
          </p>
        </div>
        <div className="activity-heatmap__legend" aria-hidden="true">
          <span className="activity-heatmap__legend-label">Less</span>
          {HEATMAP_LEGEND_COLORS.map((color) => (
            <span
              key={color}
              className="activity-heatmap__legend-swatch"
              style={{ background: color }}
            />
          ))}
          <span className="activity-heatmap__legend-label">More</span>
        </div>
      </div>

      <div className="activity-heatmap__body">
        <div className="activity-heatmap__weekday-labels" aria-hidden="true">
          {WEEKDAY_LABELS.map((label) => (
            <span key={label} className="activity-heatmap__weekday-label">
              {label}
            </span>
          ))}
        </div>

        <div
          ref={gridRef}
          className="activity-heatmap__scroll"
        >
          <div
            className="activity-heatmap__grid"
            role="grid"
            aria-label="Questions completed per day"
            onMouseMove={handleGridMouseMove}
            onMouseLeave={hideTooltip}
          >
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="activity-heatmap__week" role="row">
                {week.map((day, dayIndex) =>
                  day ? (
                    <div
                      key={day.date}
                      className="activity-heatmap__cell"
                      role="gridcell"
                      data-date={day.date}
                      style={{ background: getHeatmapCellColor(day.questions, maxQuestions) }}
                      aria-label={`${day.fullDateLabel}: ${day.questions} questions completed`}
                    />
                  ) : (
                    <div
                      key={`empty-${weekIndex}-${dayIndex}`}
                      className="activity-heatmap__cell activity-heatmap__cell--empty"
                      aria-hidden="true"
                    />
                  )
                )}
              </div>
            ))}
          </div>

          <div className="activity-heatmap__axis-labels" aria-hidden="true">
            {weeks.map((week, weekIndex) => {
              const label = axisLabels.get(weekIndex);
              return (
                <div key={weekIndex} className="activity-heatmap__axis-slot">
                  {label ? (
                    <span className="activity-heatmap__axis-label">{label}</span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div
        className={`activity-heatmap__tooltip${hoveredDay ? " activity-heatmap__tooltip--visible" : ""}`}
        style={{ left: tooltipPos.x + 12, top: tooltipPos.y - 8 }}
        role="tooltip"
        aria-hidden={!hoveredDay}
      >
        {hoveredDay ? (
          <>
            <p className="activity-heatmap__tooltip-date">{hoveredDay.fullDateLabel}</p>
            <p className="activity-heatmap__tooltip-count">
              {hoveredDay.questions} {hoveredDay.questions === 1 ? "question" : "questions"}
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ActivityHeatmap;
